import React, { useEffect, useState } from 'react';
import { ShieldAlert, Plus, Trash2, ToggleLeft, ToggleRight, Edit2 } from 'lucide-react';
import api from '../../services/api';
import FeatureLockedPage from '../../components/FeatureLockedPage';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';
import alertService from '../../utils/alert';

const TYPE_LABELS = { keyword: 'Keyword', regex: 'Regex', topic_block: 'Topic Block' };
const TYPE_COLORS = { keyword: 'bg-[#D8E6FE] text-[#0176D3]', regex: 'bg-[#D8E6FE] text-[#0176D3]', topic_block: 'bg-[#FDE4C9] text-[#DD7A01]' };
const ACTION_COLORS = { block: 'bg-[#FDDDE3] text-[#BA0517]', log_only: 'bg-[#FDE4C9] text-[#DD7A01]' };

const BLANK = { name: '', type: 'keyword', value: '', direction: 'both', action: 'block', isActive: true };

const VALUE_LABEL = { keyword: 'Keyword to block', regex: 'Regex pattern', topic_block: 'Topic description' };

export default function LlmFirewallPage() {
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [planError, setPlanError] = useState(null);

  const load = async () => {
    try {
      const [rulesRes, statsRes] = await Promise.all([
        api.get('/support/llm-firewall'),
        api.get('/support/llm-firewall/stats'),
      ]);
      setRules(rulesRes.data.rules || []);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setPlanError(err.response.data?.message || err.response.data?.error || 'This feature is not available on your current plan.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = key => val => setForm(f => ({ ...f, [key]: val }));

  const openCreate = () => { setEditing(null); setForm(BLANK); setError(''); setShowModal(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name, type: r.type, value: r.value, direction: r.direction, action: r.action, isActive: r.isActive });
    setError('');
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.value.trim()) return;
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await api.put(`/support/llm-firewall/${editing._id}`, form);
      } else {
        await api.post('/support/llm-firewall', form);
      }
      setShowModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!(await alertService.confirm('Delete?', 'Delete this firewall rule?', 'danger', 'Delete'))) return;
    await api.delete(`/support/llm-firewall/${id}`);
    load();
  };

  const toggleActive = async (rule) => {
    await api.put(`/support/llm-firewall/${rule._id}`, { isActive: !rule.isActive });
    load();
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-[#706E6B]">Loading…</div>;

  if (planError) {
    return (
      <FeatureLockedPage
        title="LLM Firewall"
        subtitle="Configure content filtering rules for your AI support chatbot."
        icon={ShieldAlert}
        featureName="LLM Firewall"
        description={planError}
      />
    );
  }

  return (
    <div className={PAGE}>
      <PageHeader
        title="LLM Firewall"
        icon={ShieldAlert}
        subtitle="Configure content filtering rules for your AI support chatbot."
        actions={<button onClick={openCreate} className="h-8 inline-flex items-center gap-2 bg-[#0176D3] hover:bg-[#014486] text-white px-3 rounded whitespace-nowrap text-[13px] font-semibold"><Plus size={14} /> New Rule</button>}
      />

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Rules', value: stats.totalRules },
            { label: 'Active Rules', value: stats.activeRules },
            { label: 'Total Blocks', value: stats.totalHits },
            { label: 'Last 7 Days Hits', value: stats.recentBlocks },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white rounded-lg border border-[#E5E5E5] p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
              <p className="text-xs text-[#706E6B]">{label}</p>
              <p className="text-2xl font-bold text-[#080707] mt-1">{value}</p>
            </div>
          ))}
        </div>
      )}

      {rules.length === 0 ? (
        <div className="text-center py-16 text-[#706E6B]">
          <ShieldAlert size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">No firewall rules yet</p>
          <p className="text-sm mt-1">Add rules to filter content going in or out of the support AI.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-[#E5E5E5] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <div className="slds2-table-wrap overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F9FB] border-b border-[#E5E5E5]">
              <tr>
                {['Name', 'Type', 'Direction', 'Action', 'Active', ''].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-medium text-[#706E6B]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F3F2F2]">
              {rules.map(r => (
                <tr key={r._id} className="hover:bg-[#F7F9FB]">
                  <td className="px-4 py-2.5 font-medium text-[#080707]">{r.name}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_COLORS[r.type]}`}>
                      {TYPE_LABELS[r.type]}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-[#5C5C5C] capitalize">{r.direction}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ACTION_COLORS[r.action]}`}>
                      {r.action === 'log_only' ? 'Log Only' : 'Block'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <button onClick={() => toggleActive(r)} className="text-[#706E6B] hover:text-[#444444]">
                      {r.isActive ? <ToggleRight size={20} className="text-[#2E844A]" /> : <ToggleLeft size={20} />}
                    </button>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(r)} className="p-1.5 text-[#706E6B] hover:text-[#0176D3] hover:bg-[#EEF4FF] rounded">
                        <Edit2 size={14} />
                      </button>
                      <button onClick={() => remove(r._id)} className="p-1.5 text-[#706E6B] hover:text-[#BA0517] hover:bg-[#FEF1EE] rounded">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-black/40">
          <div className="bg-white shadow-sm w-full max-w-md p-4 sm:p-6 rounded-t-lg sm:rounded-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-[16px] font-bold text-[#2E2E2E] mb-4">{editing ? 'Edit Rule' : 'New Firewall Rule'}</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-[12px] font-medium text-[#5C5C5C] mb-1">Name *</label>
                <input value={form.name} onChange={e => set('name')(e.target.value)}
                  placeholder="e.g. Block competitor mentions"
                  className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#0176D3]" />
              </div>
              <div>
                <label className="block text-[12px] font-medium text-[#5C5C5C] mb-1">Type</label>
                <select value={form.type} onChange={e => set('type')(e.target.value)}
                  className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#0176D3]">
                  <option value="keyword">Keyword</option>
                  <option value="regex">Regex</option>
                  <option value="topic_block">Topic Block</option>
                </select>
              </div>
              <div>
                <label className="block text-[12px] font-medium text-[#5C5C5C] mb-1">{VALUE_LABEL[form.type]} *</label>
                <input value={form.value} onChange={e => set('value')(e.target.value)}
                  placeholder={form.type === 'regex' ? 'e.g. (?i)competitor_name' : ''}
                  className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#0176D3]" />
                {form.type === 'regex' && (
                  <p className="text-xs text-[#706E6B] mt-1">e.g. (?i)competitor_name — use standard regex syntax</p>
                )}
              </div>
              <div>
                <label className="block text-[12px] font-medium text-[#5C5C5C] mb-1">Direction</label>
                <select value={form.direction} onChange={e => set('direction')(e.target.value)}
                  className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#0176D3]">
                  <option value="both">Both</option>
                  <option value="input">Input</option>
                  <option value="output">Output</option>
                </select>
              </div>
              <div>
                <label className="block text-[12px] font-medium text-[#5C5C5C] mb-1">Action</label>
                <select value={form.action} onChange={e => set('action')(e.target.value)}
                  className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#0176D3]">
                  <option value="block">Block</option>
                  <option value="log_only">Log Only</option>
                </select>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.isActive} onChange={e => set('isActive')(e.target.checked)}
                  className="w-4 h-4 rounded accent-[#0176D3]" />
                <span className="text-sm text-[#444444]">Active</span>
              </label>
              {error && <p className="text-xs text-[#BA0517]">{error}</p>}
            </div>
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-[#E5E5E5]">
              <button onClick={() => setShowModal(false)}
                className="h-9 px-4 bg-white border border-[#C9C9C9] text-[#0176D3] text-[13px] font-semibold rounded hover:bg-[#F3F2F2]">Cancel</button>
              <button onClick={save} disabled={saving || !form.name.trim() || !form.value.trim()}
                className="h-9 px-4 bg-[#0176D3] hover:bg-[#014486] disabled:opacity-50 text-white rounded text-[13px] font-semibold">
                {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Rule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
